import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, Sparkles, CheckCircle, ChevronRight, Loader2,
  ArrowLeft, Brain, Heart, Users, User, GraduationCap,
  Briefcase, Plus, Trash2, Calendar, Building2, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { MUNICIPIOS } from '../utils/cidades';

// ═══════════════════════════════════════════════════════════════
// BANCO DE PERGUNTAS — 3 seções
// ═══════════════════════════════════════════════════════════════
const SECOES = [
  {
    id: 'hard',
    titulo: 'Hard Skills',
    subtitulo: 'Competências técnicas',
    cor: 'from-blue-600 to-blue-800',
    corLight: 'bg-blue-50 text-blue-700 border-blue-200',
    icone: Brain,
    perguntas: [
      {
        chave: 'area_atuacao',
        pergunta: 'Qual é a sua principal área de atuação profissional?',
        subtitulo: 'Escolha a que melhor descreve sua carreira',
        opcoes: [
          { label: 'Tecnologia da Informação', emoji: '💻' },
          { label: 'Administração e Gestão', emoji: '📊' },
          { label: 'Saúde e Enfermagem', emoji: '🏥' },
          { label: 'Educação e Ensino', emoji: '📚' },
          { label: 'Comércio e Vendas', emoji: '🛍️' },
          { label: 'Engenharia e Construção', emoji: '🏗️' },
          { label: 'Agronegócio', emoji: '🌿' },
          { label: 'Direito e Jurídico', emoji: '⚖️' },
          { label: 'Contabilidade e Finanças', emoji: '💰' },
          { label: 'Comunicação e Marketing', emoji: '📣' },
          { label: 'Logística e Transporte', emoji: '🚛' },
          { label: 'Outra área', emoji: '🎯' },
        ],
      },
      {
        chave: 'ferramentas',
        pergunta: 'Quais ferramentas ou sistemas você domina no trabalho?',
        subtitulo: 'Escolha a principal habilidade técnica',
        opcoes: [
          { label: 'Pacote Office / Google Workspace', emoji: '📊' },
          { label: 'Sistemas ERP / CRM', emoji: '🖥️' },
          { label: 'Programação / Desenvolvimento', emoji: '👨‍💻' },
          { label: 'Redes / Infraestrutura TI', emoji: '🌐' },
          { label: 'Análise de dados / BI', emoji: '📈' },
          { label: 'Design / Edição de imagens', emoji: '🎨' },
          { label: 'Atendimento / Telemarketing', emoji: '🎧' },
          { label: 'Maquinário / Equipamentos', emoji: '⚙️' },
          { label: 'Contabilidade / Fiscal', emoji: '🧾' },
          { label: 'Redação / Produção de conteúdo', emoji: '✍️' },
          { label: 'Não tenho habilidades técnicas específicas', emoji: '🤷' },
        ],
      },
      {
        chave: 'experiencia',
        pergunta: 'Quantos anos de experiência profissional você tem?',
        subtitulo: 'Considere toda experiência, mesmo informal ou autônoma',
        opcoes: [
          { label: 'Nenhuma (primeiro emprego)', emoji: '🌱' },
          { label: '1-2 anos', emoji: '🌿' },
          { label: '3-5 anos', emoji: '🌳' },
          { label: '6-10 anos', emoji: '🏆' },
          { label: 'Mais de 10 anos', emoji: '⭐' },
        ],
      },
      {
        chave: 'nivel_escolaridade',
        pergunta: 'Qual é o seu nível de escolaridade?',
        subtitulo: 'O mais alto concluído ou em andamento',
        opcoes: [
          { label: 'Ensino Fundamental', emoji: '📖' },
          { label: 'Ensino Médio', emoji: '🏫' },
          { label: 'Técnico', emoji: '🔧' },
          { label: 'Graduação', emoji: '🎓' },
          { label: 'Pós-graduação', emoji: '📜' },
          { label: 'Mestrado / Doutorado', emoji: '🔬' },
        ],
      },
      {
        chave: 'idiomas',
        pergunta: 'Você fala algum idioma além do Português?',
        subtitulo: 'Idiomas fazem diferença no mercado',
        opcoes: [
          { label: 'Somente Português', emoji: '🇧🇷' },
          { label: 'Inglês básico', emoji: '🇺🇸' },
          { label: 'Inglês intermediário/avançado', emoji: '🌟' },
          { label: 'Espanhol', emoji: '🇪🇸' },
          { label: 'Outro idioma', emoji: '🌍' },
        ],
      },
      {
        chave: 'certificacoes',
        pergunta: 'Você possui certificações, cursos técnicos ou treinamentos?',
        subtitulo: 'Cursos extras aumentam seu score de perfil',
        opcoes: [
          { label: 'Sim, tenho certificações na minha área', emoji: '📜' },
          { label: 'Sim, cursos livres e capacitações', emoji: '🎓' },
          { label: 'Estou cursando no momento', emoji: '📚' },
          { label: 'Ainda não, mas pretendo', emoji: '🔜' },
          { label: 'Não tenho', emoji: '❌' },
        ],
      },
    ],
  },
  {
    id: 'soft',
    titulo: 'Soft Skills',
    subtitulo: 'Habilidades comportamentais',
    cor: 'from-purple-600 to-purple-800',
    corLight: 'bg-purple-50 text-purple-700 border-purple-200',
    icone: Heart,
    perguntas: [
      {
        chave: 'trabalho_equipe',
        pergunta: 'Como você se descreve trabalhando em equipe?',
        subtitulo: 'Seja honesto — não há resposta certa',
        opcoes: [
          { label: 'Sou líder natural, gosto de coordenar', emoji: '🧭' },
          { label: 'Colaboro bem, prefiro apoiar do que liderar', emoji: '🤝' },
          { label: 'Me dou bem tanto liderando quanto sendo liderado', emoji: '⚖️' },
          { label: 'Sou mais produtivo trabalhando de forma independente', emoji: '🎯' },
          { label: 'Depende do projeto e da equipe', emoji: '🔄' },
        ],
      },
      {
        chave: 'gestao_pressao',
        pergunta: 'Como você reage quando há prazo curto ou pressão alta?',
        subtitulo: 'Situações difíceis revelam seu perfil',
        opcoes: [
          { label: 'Foco total, entro em "modo turbo"', emoji: '🚀' },
          { label: 'Me organizo melhor, faço listas e priorizo', emoji: '📋' },
          { label: 'Fico estressado(a) mas entrego o resultado', emoji: '😅' },
          { label: 'Peço ajuda e delego quando necessário', emoji: '🤲' },
          { label: 'Prefiro evitar situações assim, trabalho preventivamente', emoji: '🛡️' },
        ],
      },
      {
        chave: 'comunicacao',
        pergunta: 'Qual é o seu estilo de comunicação profissional?',
        subtitulo: 'Como você se expressa no ambiente de trabalho',
        opcoes: [
          { label: 'Direto e objetivo, vou direto ao ponto', emoji: '🎯' },
          { label: 'Detalhado e formal, prefiro documentar tudo', emoji: '📝' },
          { label: 'Empático e cuidadoso com as palavras', emoji: '💬' },
          { label: 'Dinâmico, me adapto ao interlocutor', emoji: '🔄' },
          { label: 'Prefiro comunicação escrita (e-mail/chat)', emoji: '✉️' },
        ],
      },
      {
        chave: 'aprendizado',
        pergunta: 'Como você aprende melhor algo novo?',
        subtitulo: 'Cada pessoa aprende de um jeito diferente',
        opcoes: [
          { label: 'Praticando — aprendo fazendo', emoji: '⚒️' },
          { label: 'Observando — vejo alguém fazer antes', emoji: '👀' },
          { label: 'Estudando — leitura e cursos teóricos', emoji: '📚' },
          { label: 'Errando — erro, corrijo e evoluo', emoji: '💪' },
          { label: 'Em grupo — aprender com os outros', emoji: '👥' },
        ],
      },
      {
        chave: 'resolucao_conflito',
        pergunta: 'Como você lida com conflitos ou discordâncias no trabalho?',
        subtitulo: 'A forma de resolver conflitos diz muito sobre você',
        opcoes: [
          { label: 'Converso diretamente com a pessoa envolvida', emoji: '🗣️' },
          { label: 'Busco mediação de um superior ou RH', emoji: '🤝' },
          { label: 'Evito o conflito e foco no resultado', emoji: '🎯' },
          { label: 'Ouço todos os lados antes de me posicionar', emoji: '👂' },
          { label: 'Sou assertivo(a) e defendo meu ponto de vista', emoji: '💼' },
        ],
      },
      {
        chave: 'proatividade',
        pergunta: 'Quando percebe um problema ou melhoria possível no trabalho, o que você faz?',
        subtitulo: 'Seu nível de iniciativa profissional',
        opcoes: [
          { label: 'Ajo imediatamente e comunico depois', emoji: '⚡' },
          { label: 'Proponho a solução ao meu gestor antes de agir', emoji: '📣' },
          { label: 'Discuto com a equipe para alinhar expectativas', emoji: '💡' },
          { label: 'Aguardo ser solicitado para agir', emoji: '⏳' },
          { label: 'Depende da situação e do impacto', emoji: '🔄' },
        ],
      },
    ],
  },
  {
    id: 'fit',
    titulo: 'Fit Cultural',
    subtitulo: 'Valores e ambiente ideal',
    cor: 'from-emerald-600 to-emerald-800',
    corLight: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icone: Users,
    perguntas: [
      {
        chave: 'ambiente_ideal',
        pergunta: 'Como é o ambiente de trabalho dos seus sonhos?',
        subtitulo: 'O ambiente certo faz toda a diferença',
        opcoes: [
          { label: 'Inovador, dinâmico e com muita mudança', emoji: '🚀' },
          { label: 'Estruturado, com processos claros e estabilidade', emoji: '🏛️' },
          { label: 'Colaborativo, com foco em equipe e pessoas', emoji: '🤝' },
          { label: 'Autônomo, com liberdade para criar e decidir', emoji: '🦅' },
          { label: 'Focado em resultados, meritocrático', emoji: '🏆' },
          { label: 'Social, com propósito e impacto na comunidade', emoji: '💚' },
        ],
      },
      {
        chave: 'lideranca_preferida',
        pergunta: 'Que tipo de liderança você funciona melhor?',
        subtitulo: 'O estilo do gestor influencia muito seu desempenho',
        opcoes: [
          { label: 'Liderança próxima, com feedback constante', emoji: '🧑‍🏫' },
          { label: 'Liderança que dá autonomia e cobra resultados', emoji: '🦅' },
          { label: 'Liderança democrática, que ouve a equipe', emoji: '🗣️' },
          { label: 'Liderança técnica, que ensina e orienta', emoji: '🧠' },
          { label: 'Me adapto a qualquer estilo de gestão', emoji: '🔄' },
        ],
      },
      {
        chave: 'valores',
        pergunta: 'Qual valor é mais importante para você em uma empresa?',
        subtitulo: 'Alinhamento de valores é fundamental para felicidade no trabalho',
        opcoes: [
          { label: 'Ética e transparência', emoji: '⚖️' },
          { label: 'Inovação e criatividade', emoji: '💡' },
          { label: 'Diversidade e inclusão', emoji: '🌈' },
          { label: 'Desenvolvimento e crescimento', emoji: '📈' },
          { label: 'Equilíbrio vida-trabalho', emoji: '⚖️' },
          { label: 'Impacto social e sustentabilidade', emoji: '🌱' },
        ],
      },
      {
        chave: 'motivacao',
        pergunta: 'O que mais te motiva no trabalho?',
        subtitulo: 'Sua motivação define seu engajamento',
        opcoes: [
          { label: 'Reconhecimento e salário competitivo', emoji: '💰' },
          { label: 'Aprender e me desenvolver constantemente', emoji: '📚' },
          { label: 'Fazer parte de algo com propósito maior', emoji: '🌟' },
          { label: 'Boas relações e clima organizacional', emoji: '😊' },
          { label: 'Autonomia para tomar decisões', emoji: '🎯' },
          { label: 'Estabilidade e segurança profissional', emoji: '🛡️' },
        ],
      },
      {
        chave: 'modalidade',
        pergunta: 'Qual formato de trabalho você prefere?',
        subtitulo: 'Isso será usado no algoritmo de match',
        opcoes: [
          { label: 'Presencial em Rio Branco', emoji: '🏙️' },
          { label: 'Presencial em outra cidade', emoji: '🗺️' },
          { label: 'Remoto / home office', emoji: '🏠' },
          { label: 'Híbrido', emoji: '🔀' },
          { label: 'Qualquer um', emoji: '✅' },
        ],
      },
    ],
  },
];

const TOTAL_PERGUNTAS = SECOES.reduce((t, s) => t + s.perguntas.length, 0);

// ═══════════════════════════════════════════════════════════════
// SUBCOMPONENTES
// ═══════════════════════════════════════════════════════════════

function BolhaIA({ texto, animando }) {
  const [exibido, setExibido] = useState('');
  const [cursor, setCursor] = useState(true);
  useEffect(() => {
    if (!animando) { setExibido(texto); setCursor(false); return; }
    setExibido(''); setCursor(true);
    let i = 0;
    const iv = setInterval(() => {
      i++; setExibido(texto.slice(0, i));
      if (i >= texto.length) { clearInterval(iv); setCursor(false); }
    }, 16);
    return () => clearInterval(iv);
  }, [texto, animando]);
  return (
    <div className="flex gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center flex-shrink-0 shadow-sm">
        <Bot size={18} className="text-white" />
      </div>
      <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex-1">
        <p className="text-sm text-gray-700 leading-relaxed">
          {exibido}
          {cursor && <span className="inline-block w-0.5 h-4 bg-acre-green ml-0.5 animate-pulse align-middle" />}
        </p>
      </div>
    </div>
  );
}

function SecaoBadge({ secao, pequeno }) {
  const Icon = secao.icone;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold border rounded-full ${
      pequeno ? 'text-xs px-2 py-0.5' : 'text-xs px-2.5 py-1'
    } ${secao.corLight}`}>
      <Icon size={pequeno ? 10 : 11} /> {secao.titulo}
    </span>
  );
}

// ── Chip de skill ──────────────────────────────────────────────
function SkillChip({ label, onRemove, cor }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cor}`}>
      {label}
      {onRemove && (
        <button onClick={onRemove} className="hover:opacity-70">
          <X size={10}/>
        </button>
      )}
    </span>
  );
}

// ── Tela de introdução da seção ────────────────────────────────
function TelaSecao({ secao, numero, onIniciar }) {
  const Icon = secao.icone;
  return (
    <div className="px-6 pb-6 text-center">
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${secao.cor} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
        <Icon size={30} className="text-white" />
      </div>
      <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-500 text-xs font-semibold px-3 py-1 rounded-full mb-3">
        Seção {numero} de 3
      </div>
      <h3 className="font-sora font-bold text-acre-dark text-xl mb-1">{secao.titulo}</h3>
      <p className="text-gray-500 text-sm mb-2">{secao.subtitulo}</p>
      <p className="text-gray-400 text-xs mb-6 leading-relaxed">
        {secao.perguntas.length} perguntas sobre{' '}
        {secao.id === 'hard' ? 'suas habilidades técnicas, experiência e formação.' :
         secao.id === 'soft' ? 'seu comportamento, comunicação e forma de trabalhar.' :
         'seus valores, ambiente ideal e motivações profissionais.'}
      </p>
      <button onClick={onIniciar}
        className={`w-full text-white font-sora font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-opacity hover:opacity-90 shadow-sm text-sm bg-gradient-to-r ${secao.cor}`}>
        <Sparkles size={15} /> Começar {secao.titulo}
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

// ── Tela gerando ───────────────────────────────────────────────
function TelaGerando() {
  const msgs = [
    '🧠 Analisando suas hard skills…',
    '💬 Identificando seus soft skills…',
    '🌱 Avaliando seu fit cultural…',
    '✨ Gerando seu perfil profissional…',
    '🎯 Finalizando com IA…',
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setIdx(p => (p + 1) % msgs.length), 1200);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center shadow-lg">
          <Sparkles size={34} className="text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-7 h-7 bg-acre-gold rounded-full flex items-center justify-center">
          <Loader2 size={15} className="text-white animate-spin" />
        </div>
      </div>
      <h3 className="font-sora font-bold text-acre-dark text-lg mb-2">IA criando seu perfil</h3>
      <p className="text-sm text-gray-500 min-h-[20px]">{msgs[idx]}</p>
      <div className="flex gap-1.5 mt-5">
        {[0,1,2].map(i => (
          <span key={i} className="w-2.5 h-2.5 bg-acre-green rounded-full animate-bounce"
            style={{ animationDelay: `${i * 150}ms` }} />
        ))}
      </div>
    </div>
  );
}

// ── Resultado IA — 3 cards ────────────────────────────────────
function TelaResultado({ perfil, onConfirmar, confirmando }) {
  return (
    <div className="px-5 pb-6 space-y-4">
      <div className="text-center mb-2">
        <div className="w-12 h-12 rounded-2xl bg-acre-green/10 flex items-center justify-center mx-auto mb-2">
          <Sparkles size={22} className="text-acre-green" />
        </div>
        <p className="font-sora font-bold text-acre-dark">Seu perfil foi analisado!</p>
        <p className="text-xs text-gray-400 mt-0.5">Revise os resultados antes de confirmar</p>
      </div>

      {/* Hard Skills */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <Brain size={14} className="text-blue-600"/>
          <p className="font-sora font-bold text-sm text-blue-800">Hard Skills</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(perfil.hard_skills || []).map((s, i) => (
            <SkillChip key={i} label={s} cor="bg-blue-100 text-blue-700 border-blue-200"/>
          ))}
          {(!perfil.hard_skills?.length) && <p className="text-xs text-blue-400 italic">Nenhuma identificada</p>}
        </div>
      </div>

      {/* Soft Skills */}
      <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <Heart size={14} className="text-purple-600"/>
          <p className="font-sora font-bold text-sm text-purple-800">Soft Skills</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(perfil.soft_skills || []).map((s, i) => (
            <SkillChip key={i} label={s} cor="bg-purple-100 text-purple-700 border-purple-200"/>
          ))}
          {(!perfil.soft_skills?.length) && <p className="text-xs text-purple-400 italic">Nenhuma identificada</p>}
        </div>
      </div>

      {/* Fit Cultural */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Users size={14} className="text-emerald-600"/>
          <p className="font-sora font-bold text-sm text-emerald-800">Fit Cultural</p>
        </div>
        <p className="text-xs text-emerald-700 leading-relaxed">{perfil.fit_cultural || '—'}</p>
      </div>

      {/* Descrição geral */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <p className="text-xs font-semibold text-gray-500 mb-1.5">📝 Resumo profissional</p>
        <p className="text-xs text-gray-700 leading-relaxed">{perfil.descricao}</p>
      </div>

      <button onClick={onConfirmar} disabled={confirmando}
        className="w-full bg-acre-green text-white font-sora font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-acre-mid transition-colors disabled:opacity-60 text-sm shadow-sm">
        {confirmando
          ? <><Loader2 size={15} className="animate-spin"/>Salvando…</>
          : <><CheckCircle size={15}/>Confirmar perfil e continuar →</>}
      </button>
    </div>
  );
}

// ── Formulário: Dados Pessoais ────────────────────────────────
function FormDadosPessoais({ dados, onChange, onAvancar, onVoltar }) {
  return (
    <div className="px-5 pb-6 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center">
          <User size={16} className="text-green-600"/>
        </div>
        <div>
          <p className="font-sora font-bold text-sm text-acre-dark">Dados Pessoais</p>
          <p className="text-xs text-gray-400">Informações básicas para contato</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label">WhatsApp *</label>
          <input className="input" placeholder="68 9 9999-9999"
            value={dados.whatsapp} onChange={e => onChange('whatsapp', e.target.value)}/>
          <p className="text-xs text-gray-400 mt-0.5">Empresas entrarão em contato por aqui</p>
        </div>
        <div>
          <label className="label">Data de nascimento</label>
          <input className="input" type="date" value={dados.data_nascimento}
            onChange={e => onChange('data_nascimento', e.target.value)}/>
        </div>
        <div>
          <label className="label">Gênero</label>
          <select className="input" value={dados.genero} onChange={e => onChange('genero', e.target.value)}>
            <option value="">Prefiro não dizer</option>
            <option value="masculino">Masculino</option>
            <option value="feminino">Feminino</option>
            <option value="nao_binario">Não binário</option>
            <option value="outro">Outro</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Cidade *</label>
          <select className="input" value={dados.cidade} onChange={e => onChange('cidade', e.target.value)}>
            <option value="">Selecione sua cidade…</option>
            {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">CPF</label>
          <input className="input" placeholder="000.000.000-00"
            value={dados.cpf} onChange={e => onChange('cpf', e.target.value)}/>
          <p className="text-xs text-gray-400 mt-0.5">Opcional — necessário para algumas vagas</p>
        </div>
      </div>

      <div className="flex gap-3 pt-1">
        <button onClick={onVoltar} className="btn-outline px-4 py-2.5 text-sm flex items-center gap-1.5">
          <ArrowLeft size={14}/> Voltar
        </button>
        <button onClick={onAvancar} className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5">
          Próximo <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  );
}

// ── Formulário: Formação ──────────────────────────────────────
function FormFormacao({ dados, onChange, onAvancar, onVoltar }) {
  return (
    <div className="px-5 pb-6 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
          <GraduationCap size={16} className="text-indigo-600"/>
        </div>
        <div>
          <p className="font-sora font-bold text-sm text-acre-dark">Formação Acadêmica</p>
          <p className="text-xs text-gray-400">Sua escolaridade e curso principal</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label">Nível de escolaridade *</label>
          <select className="input" value={dados.nivel} onChange={e => onChange('nivel', e.target.value)}>
            <option value="">Selecione…</option>
            {['Ensino Fundamental','Ensino Médio','Técnico','Graduação','Pós-graduação','Mestrado','Doutorado'].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Curso / Área de formação</label>
          <input className="input" placeholder="Ex: Administração, Enfermagem, Sistemas…"
            value={dados.curso} onChange={e => onChange('curso', e.target.value)}/>
        </div>
        <div>
          <label className="label">Instituição de ensino</label>
          <input className="input" placeholder="Ex: UFAC, IFAC, Unopar…"
            value={dados.instituicao} onChange={e => onChange('instituicao', e.target.value)}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Ano de conclusão</label>
            <input className="input" type="number" min="1980" max="2030" placeholder="2024"
              value={dados.ano_conclusao} onChange={e => onChange('ano_conclusao', e.target.value)}/>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="accent-acre-green w-4 h-4"
                checked={dados.em_andamento}
                onChange={e => onChange('em_andamento', e.target.checked)}/>
              <span className="text-sm text-gray-600">Em andamento</span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-1">
        <button onClick={onVoltar} className="btn-outline px-4 py-2.5 text-sm flex items-center gap-1.5">
          <ArrowLeft size={14}/> Voltar
        </button>
        <button onClick={onAvancar} className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5">
          Próximo <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  );
}

// ── Formulário: Experiências ──────────────────────────────────
const EXP_VAZIA = { empresa: '', cargo: '', data_inicio: '', data_fim: '', descricao: '', atual: false };

function FormExperiencias({ exps, setExps, onAvancar, onVoltar, onPular }) {
  const add = () => {
    if (exps.length >= 3) return toast.error('Máximo 3 experiências.');
    setExps(p => [...p, { ...EXP_VAZIA }]);
  };
  const rem = (i) => setExps(p => p.filter((_, idx) => idx !== i));
  const upd = (i, k, v) => setExps(p => p.map((e, idx) => idx === i ? { ...e, [k]: v } : e));

  return (
    <div className="px-5 pb-6 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
          <Briefcase size={16} className="text-orange-600"/>
        </div>
        <div>
          <p className="font-sora font-bold text-sm text-acre-dark">Experiências Profissionais</p>
          <p className="text-xs text-gray-400">Adicione até 3 experiências (opcional)</p>
        </div>
      </div>

      {exps.length === 0 && (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-6 text-center">
          <Briefcase size={24} className="text-gray-300 mx-auto mb-2"/>
          <p className="text-sm text-gray-500 mb-1">Nenhuma experiência adicionada</p>
          <p className="text-xs text-gray-400">Você pode adicionar depois no seu perfil</p>
        </div>
      )}

      {exps.map((exp, i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Experiência {i + 1}</p>
            <button onClick={() => rem(i)} className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-400 transition-colors">
              <Trash2 size={12}/>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Empresa</label>
              <input className="input text-sm" placeholder="Nome da empresa"
                value={exp.empresa} onChange={e => upd(i, 'empresa', e.target.value)}/>
            </div>
            <div>
              <label className="label">Cargo</label>
              <input className="input text-sm" placeholder="Seu cargo"
                value={exp.cargo} onChange={e => upd(i, 'cargo', e.target.value)}/>
            </div>
            <div>
              <label className="label">Início</label>
              <input className="input text-sm" type="month"
                value={exp.data_inicio} onChange={e => upd(i, 'data_inicio', e.target.value)}/>
            </div>
            <div>
              <label className="label">Fim</label>
              {exp.atual
                ? <p className="text-sm text-acre-green font-semibold mt-2">Emprego atual</p>
                : <input className="input text-sm" type="month"
                    value={exp.data_fim} onChange={e => upd(i, 'data_fim', e.target.value)}/>}
              <label className="flex items-center gap-1.5 cursor-pointer mt-1">
                <input type="checkbox" className="accent-acre-green" checked={exp.atual}
                  onChange={e => upd(i, 'atual', e.target.checked)}/>
                <span className="text-xs text-gray-500">Atual</span>
              </label>
            </div>
          </div>
          <div>
            <label className="label">Descrição das atividades</label>
            <textarea className="input text-sm min-h-[60px] resize-none" placeholder="O que você fazia nessa função…"
              value={exp.descricao} onChange={e => upd(i, 'descricao', e.target.value)}/>
          </div>
        </div>
      ))}

      {exps.length < 3 && (
        <button onClick={add}
          className="w-full border-2 border-dashed border-gray-200 hover:border-acre-green rounded-2xl py-3 text-sm text-gray-400 hover:text-acre-green flex items-center justify-center gap-2 transition-colors">
          <Plus size={15}/> Adicionar experiência
        </button>
      )}

      <div className="flex gap-3 pt-1">
        <button onClick={onVoltar} className="btn-outline px-4 py-2.5 text-sm flex items-center gap-1.5">
          <ArrowLeft size={14}/> Voltar
        </button>
        <button onClick={onPular} className="btn-outline px-4 py-2.5 text-sm text-gray-400">
          Pular
        </button>
        <button onClick={onAvancar} className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5">
          Concluir <CheckCircle size={14}/>
        </button>
      </div>
    </div>
  );
}

// ── Tela final ────────────────────────────────────────────────
function TelaConclusao({ nome, onEntrar }) {
  return (
    <div className="px-6 pb-8 text-center">
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center mx-auto mb-5 shadow-lg">
        <CheckCircle size={36} className="text-white" />
      </div>
      <h3 className="font-sora font-bold text-acre-dark text-2xl mb-2">
        Perfil criado! 🎉
      </h3>
      <p className="text-gray-500 text-sm mb-6 leading-relaxed">
        Parabéns, <strong>{nome}</strong>! Seu perfil profissional está pronto.<br/>
        Agora você pode explorar as vagas compatíveis com seu perfil.
      </p>
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { icon: Brain, label: 'Hard Skills', cor: 'text-blue-600 bg-blue-50' },
          { icon: Heart, label: 'Soft Skills', cor: 'text-purple-600 bg-purple-50' },
          { icon: Users, label: 'Fit Cultural', cor: 'text-emerald-600 bg-emerald-50' },
        ].map(({ icon: Icon, label, cor }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-3 text-center">
            <div className={`w-8 h-8 rounded-lg ${cor} flex items-center justify-center mx-auto mb-1`}>
              <Icon size={15}/>
            </div>
            <p className="text-xs font-semibold text-gray-600">{label}</p>
          </div>
        ))}
      </div>
      <button onClick={onEntrar}
        className="w-full bg-gradient-to-r from-acre-green to-acre-teal text-white font-sora font-bold py-4 rounded-xl flex items-center justify-center gap-2 text-sm hover:opacity-90 transition-opacity shadow-md">
        <Sparkles size={16}/> Ver vagas para mim
        <ChevronRight size={16}/>
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════
export default function OnboardingWizard({ nome, onConcluir, modoAtualizacao = false }) {
  // Fases: boas_vindas | intro_hard | quiz | intro_soft | intro_fit | gerando | resultado | dados_pessoais | formacao | experiencias | conclusao
  const [fase,          setFase]          = useState('boas_vindas');
  const [secaoIdx,      setSecaoIdx]      = useState(0); // 0=hard, 1=soft, 2=fit
  const [perguntaIdx,   setPerguntaIdx]   = useState(0);
  const [animando,      setAnimando]      = useState(false);
  const [respostas,     setRespostas]     = useState([]);
  const [perfilIA,      setPerfilIA]      = useState(null);
  const [confirmando,   setConfirmando]   = useState(false);
  const [salvando,      setSalvando]      = useState(false);

  // Dados dos formulários
  const [dadosPessoais, setDadosPessoais] = useState({
    whatsapp: '', data_nascimento: '', cidade: '', genero: '', cpf: '',
  });
  const [formacao, setFormacao] = useState({
    nivel: '', curso: '', instituicao: '', ano_conclusao: '', em_andamento: false,
  });
  const [experiencias, setExperiencias] = useState([]);

  const scrollRef = useRef(null);
  const secaoAtual = SECOES[secaoIdx];

  // Progresso geral
  const perguntasRespondidas = respostas.length;
  const pct = Math.round((perguntasRespondidas / TOTAL_PERGUNTAS) * 100);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fase, perguntaIdx, secaoIdx]);

  // ── Responder pergunta ────────────────────────────────────────
  const responder = useCallback(async (opcao) => {
    const pergunta = secaoAtual.perguntas[perguntaIdx];
    const novaResp = { chave: pergunta.chave, resposta: opcao.label, secao: secaoAtual.id };
    const todasRespostas = [...respostas, novaResp];
    setRespostas(todasRespostas);

    const isUltimaPerguntaSecao = perguntaIdx === secaoAtual.perguntas.length - 1;
    const isUltimaSecao = secaoIdx === SECOES.length - 1;

    if (!isUltimaPerguntaSecao) {
      setAnimando(true);
      setTimeout(() => { setPerguntaIdx(p => p + 1); setAnimando(false); }, 250);
    } else if (!isUltimaSecao) {
      // Avança para próxima seção
      setAnimando(true);
      setTimeout(() => {
        setSecaoIdx(p => p + 1);
        setPerguntaIdx(0);
        setFase(`intro_secao`);
        setAnimando(false);
      }, 250);
    } else {
      // Todas as perguntas respondidas → gerar perfil
      setFase('gerando');
      try {
        const { data } = await api.post('/candidatos/ia/gerar-perfil', { respostas: todasRespostas });
        setPerfilIA(data);
        setTimeout(() => setFase('resultado'), 700);
      } catch {
        toast.error('Erro ao gerar perfil. Tente novamente.');
        setFase('quiz');
      }
    }
  }, [respostas, secaoAtual, secaoIdx, perguntaIdx]);

  // ── Confirmar perfil IA → ir para dados pessoais (ou salvar direto no modo atualização) ──
  const confirmarPerfil = () => {
    if (modoAtualizacao) {
      // No modo atualização só salva o perfil IA sem sobrescrever dados pessoais/formação/exp
      salvarSomenteIA();
    } else {
      setFase('dados_pessoais');
    }
  };

  const salvarSomenteIA = useCallback(async () => {
    setConfirmando(true);
    try {
      await api.post('/candidatos/ia/concluir-onboarding', {
        descricao:          perfilIA.descricao,
        hard_skills:        perfilIA.hard_skills,
        soft_skills:        perfilIA.soft_skills,
        fit_cultural:       perfilIA.fit_cultural,
        areas_interesse:    perfilIA.areas_interesse,
        nivel_escolaridade: perfilIA.nivel_escolaridade,
        experiencia_anos:   perfilIA.experiencia_anos,
        modalidade:         perfilIA.modalidade,
        disponibilidade:    perfilIA.disponibilidade,
        pretensao_salarial: perfilIA.pretensao_salarial,
        dados_pessoais:     null,
        formacao:           null,
        experiencias:       [],
      });
      toast.success('🎉 Perfil atualizado com sucesso!');
      setFase('conclusao');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar.');
    }
    setConfirmando(false);
  }, [perfilIA]);

  // ── Salvar tudo ───────────────────────────────────────────────
  const salvarTudo = useCallback(async (expsFinal) => {
    setSalvando(true);
    try {
      await api.post('/candidatos/ia/concluir-onboarding', {
        descricao:          perfilIA.descricao,
        hard_skills:        perfilIA.hard_skills,
        soft_skills:        perfilIA.soft_skills,
        fit_cultural:       perfilIA.fit_cultural,
        areas_interesse:    perfilIA.areas_interesse,
        nivel_escolaridade: perfilIA.nivel_escolaridade || formacao.nivel || null,
        experiencia_anos:   perfilIA.experiencia_anos,
        modalidade:         perfilIA.modalidade,
        disponibilidade:    perfilIA.disponibilidade,
        pretensao_salarial: perfilIA.pretensao_salarial,
        dados_pessoais:     dadosPessoais,
        formacao:           formacao.nivel || formacao.curso ? formacao : null,
        experiencias:       (expsFinal || experiencias).filter(e => e.empresa || e.cargo),
      });
      toast.success('🎉 Perfil criado com sucesso!');
      setFase('conclusao');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    }
    setSalvando(false);
  }, [perfilIA, dadosPessoais, formacao, experiencias]);

  const secaoNumero = secaoIdx + 1;
  const perguntaAtual = secaoAtual?.perguntas[perguntaIdx];

  // ── Header cor dinâmica ───────────────────────────────────────
  const headerGrad = fase === 'quiz' ? secaoAtual.cor
    : fase.includes('intro') ? SECOES[secaoIdx]?.cor || 'from-acre-dark to-acre-green'
    : 'from-acre-dark to-acre-green';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">

        {/* ── Header ── */}
        <div className={`bg-gradient-to-r ${headerGrad} px-5 pt-5 pb-4 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <Bot size={18} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="font-sora font-bold text-white text-sm">Assistente IA — AcreJob</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                <span className="text-white/60 text-xs">
                  {fase === 'quiz' ? `${secaoAtual.titulo} — Pergunta ${perguntaIdx + 1} de ${secaoAtual.perguntas.length}` :
                   fase === 'gerando' ? 'Gerando seu perfil…' :
                   fase === 'resultado' ? 'Perfil gerado!' :
                   fase === 'dados_pessoais' ? 'Dados pessoais' :
                   fase === 'formacao' ? 'Formação acadêmica' :
                   fase === 'experiencias' ? 'Experiências' :
                   fase === 'conclusao' ? 'Perfil completo!' :
                   'Análise de perfil profissional'}
                </span>
              </div>
            </div>
            {/* Badges de seção no quiz */}
            {fase === 'quiz' && (
              <SecaoBadge secao={secaoAtual} pequeno/>
            )}
          </div>

          {/* Barra de progresso */}
          {(fase === 'quiz' || fase.includes('intro_')) && (
            <div className="mt-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-white/60 text-xs">{perguntasRespondidas} de {TOTAL_PERGUNTAS} perguntas</span>
                <span className="text-white/80 text-xs font-bold">{pct}%</span>
              </div>
              <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-acre-gold rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }} />
              </div>
              {/* Marcadores de seção */}
              <div className="flex justify-between mt-1.5">
                {SECOES.map((s, i) => (
                  <span key={s.id} className={`text-xs ${i < secaoIdx || (i === secaoIdx && fase === 'quiz') ? 'text-white/80' : 'text-white/30'}`}>
                    {s.titulo}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Progresso por etapas nos formulários */}
          {['dados_pessoais','formacao','experiencias'].includes(fase) && (
            <div className="flex items-center gap-2 mt-3">
              {[
                { id: 'dados_pessoais', label: 'Dados' },
                { id: 'formacao',       label: 'Formação' },
                { id: 'experiencias',   label: 'Experiência' },
              ].map((etapa, i) => (
                <React.Fragment key={etapa.id}>
                  <div className={`flex items-center gap-1 ${fase === etapa.id ? 'text-white' : 'text-white/40'}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                      fase === etapa.id ? 'bg-white text-acre-green' : 'bg-white/20 text-white/60'
                    }`}>{i + 1}</div>
                    <span className="text-xs hidden sm:inline">{etapa.label}</span>
                  </div>
                  {i < 2 && <div className="flex-1 h-px bg-white/20"/>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* ── Conteúdo rolável ── */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">

          {/* Boas-vindas */}
          {fase === 'boas_vindas' && (
            <div className="px-6 pb-6 pt-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center mx-auto mb-4 shadow-md">
                <Bot size={30} className="text-white" />
              </div>
              {modoAtualizacao ? (
                <>
                  <h3 className="font-sora font-bold text-acre-dark text-xl mb-2">Atualizar perfil, {nome}! 🔄</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">
                    Vou refazer sua análise com <strong>{TOTAL_PERGUNTAS} perguntas</strong>. As suas respostas anteriores serão substituídas pelo novo perfil gerado.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-sora font-bold text-acre-dark text-xl mb-2">Olá, {nome}! 👋</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">
                    Sou a <strong className="text-acre-green">IA do AcreJob</strong>. Vou criar seu perfil profissional completo com <strong>{TOTAL_PERGUNTAS} perguntas</strong> divididas em 3 seções.
                  </p>
                </>
              )}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {SECOES.map((s) => {
                  const Icon = s.icone;
                  return (
                    <div key={s.id} className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${s.cor} flex items-center justify-center mx-auto mb-1.5`}>
                        <Icon size={14} className="text-white"/>
                      </div>
                      <p className="text-xs font-semibold text-gray-700">{s.titulo}</p>
                      <p className="text-xs text-gray-400">{s.perguntas.length} perguntas</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-gray-400 text-xs mb-5 leading-relaxed">
                {modoAtualizacao
                  ? 'Seus dados pessoais, formação e experiências existentes serão mantidos. Leva cerca de 3-5 minutos.'
                  : 'Após as perguntas, você preencherá dados pessoais, formação e experiências. Leva cerca de 5-8 minutos.'}
              </p>
              <button onClick={() => setFase('intro_secao')}
                className="w-full bg-gradient-to-r from-acre-green to-acre-teal text-white font-sora font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-sm text-sm">
                <Sparkles size={16}/>
                {modoAtualizacao ? 'Refazer análise de perfil' : 'Criar meu perfil profissional'}
                <ChevronRight size={16}/>
              </button>
            </div>
          )}

          {/* Intro seção */}
          {fase === 'intro_secao' && (
            <div className="pt-5">
              <TelaSecao
                secao={secaoAtual}
                numero={secaoNumero}
                onIniciar={() => setFase('quiz')}
              />
            </div>
          )}

          {/* Quiz */}
          {fase === 'quiz' && perguntaAtual && (
            <div className="px-5 pt-5 pb-6">
              <BolhaIA
                key={`${secaoIdx}-${perguntaIdx}`}
                texto={perguntaAtual.pergunta}
                animando={perguntaIdx > 0 || secaoIdx > 0}
              />
              <p className="text-xs text-gray-400 mb-3 ml-12">{perguntaAtual.subtitulo}</p>
              <div className="space-y-2">
                {perguntaAtual.opcoes.map((opcao, idx) => (
                  <button key={idx} onClick={() => responder(opcao)}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-gray-100 bg-white hover:border-acre-green hover:bg-acre-light active:scale-[0.98] transition-all group">
                    <span className="text-xl flex-shrink-0 w-7 text-center">{opcao.emoji}</span>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-acre-dark">{opcao.label}</span>
                    <ChevronRight size={14} className="ml-auto text-gray-300 group-hover:text-acre-green flex-shrink-0"/>
                  </button>
                ))}
              </div>
              {respostas.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-300 mb-2">Seção atual: {respostas.filter(r => r.secao === secaoAtual.id).length} / {secaoAtual.perguntas.length} respondidas</p>
                  <div className="flex flex-wrap gap-1.5">
                    {respostas.filter(r => r.secao === secaoAtual.id).map((r, i) => (
                      <span key={i} className={`text-xs px-2.5 py-0.5 rounded-full border font-medium ${secaoAtual.corLight}`}>
                        {r.resposta.length > 22 ? r.resposta.slice(0, 22) + '…' : r.resposta}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gerando */}
          {fase === 'gerando' && <TelaGerando />}

          {/* Resultado */}
          {fase === 'resultado' && perfilIA && (
            <div className="pt-4">
              <TelaResultado
                perfil={perfilIA}
                onConfirmar={confirmarPerfil}
                confirmando={confirmando}
              />
            </div>
          )}

          {/* Dados pessoais */}
          {fase === 'dados_pessoais' && (
            <div className="pt-4">
              <FormDadosPessoais
                dados={dadosPessoais}
                onChange={(k, v) => setDadosPessoais(p => ({ ...p, [k]: v }))}
                onAvancar={() => setFase('formacao')}
                onVoltar={() => setFase('resultado')}
              />
            </div>
          )}

          {/* Formação */}
          {fase === 'formacao' && (
            <div className="pt-4">
              <FormFormacao
                dados={formacao}
                onChange={(k, v) => setFormacao(p => ({ ...p, [k]: v }))}
                onAvancar={() => setFase('experiencias')}
                onVoltar={() => setFase('dados_pessoais')}
              />
            </div>
          )}

          {/* Experiências */}
          {fase === 'experiencias' && (
            <div className="pt-4">
              <FormExperiencias
                exps={experiencias}
                setExps={setExperiencias}
                onAvancar={() => salvarTudo(experiencias)}
                onVoltar={() => setFase('formacao')}
                onPular={() => salvarTudo([])}
              />
              {salvando && (
                <div className="mx-5 mb-4 bg-acre-light border border-acre-green/20 rounded-xl p-3 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-acre-green"/>
                  <span className="text-sm text-acre-green font-medium">Salvando seu perfil…</span>
                </div>
              )}
            </div>
          )}

          {/* Conclusão */}
          {fase === 'conclusao' && (
            <div className="pt-5">
              <TelaConclusao nome={nome} onEntrar={onConcluir} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
